import {
  Component, ElementRef, EventEmitter, Input, Output, ViewChild,
  OnChanges, OnDestroy, SimpleChanges
} from '@angular/core';
import { CubeFillRound } from '../../../interfaces/cube-fill.interface';

interface Seg { x1: number; y1: number; x2: number; y2: number; }
interface Bubble { x: number; y: number; r: number; delay: number; dur: number; }

/**
 * Cube Fill play surface — a 3-D glass fish-tank of unit cubes (cabinet-oblique
 * projection) beside a graduated measuring gauge. Drag the gauge to set how
 * many cubic units of water to use; POUR (triggered by the shell) fills the
 * tank to a height of N / (l·w) units. An exact fill (N = l·w·h) fills to the
 * brim → win (a fish swims in); too little leaves it short, too much overflows.
 *
 * All quantities are integer cubic units so the comparison is exact.
 */
@Component({
  selector: 'app-cube-fill-stage',
  templateUrl: './cube-fill-stage.component.html',
  styleUrls: ['./cube-fill-stage.component.scss']
})
export class CubeFillStageComponent implements OnChanges, OnDestroy {
  @Input() round: CubeFillRound | null = null;
  @Input() roundKey = 0;
  @Input() interactive = true;
  @Input() currLang: any = 'en';

  @Output() solved = new EventEmitter<void>();
  @Output() missed = new EventEmitter<void>();

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  readonly VIEW_W = 1000;
  readonly VIEW_H = 560;

  // Tank drawing region + oblique projection factors.
  readonly TX0 = 60; readonly TX1 = 600;
  readonly TY0 = 90; readonly TY1 = 500;
  readonly U_MAX = 74;
  readonly AZX = 0.5;    // depth → screen x
  readonly AZY = 0.32;   // depth → screen y

  // Gauge geometry.
  readonly GX = 792; readonly GW = 74;
  readonly GY_TOP = 96; readonly GY_BOT = 486;

  /** Little bubbles that rise inside the gauge liquid (presentation only). */
  readonly gaugeBubbles = [
    { x: this.GX + this.GW * 0.30, r: 3.5, delay: 0, dur: 2700 },
    { x: this.GX + this.GW * 0.55, r: 2.5, delay: 800, dur: 2200 },
    { x: this.GX + this.GW * 0.70, r: 3, delay: 1400, dur: 2900 },
    { x: this.GX + this.GW * 0.45, r: 2, delay: 2000, dur: 2400 }
  ];

  /** Chosen amount of water, in cubic units. */
  n = 0;
  /** Water height currently drawn in the tank, in units (animates on pour). */
  fillH = 0;
  isPouring = false;
  solvedState = false;
  overflowed = false;
  shortfall = false;
  bubbles: Bubble[] = [];

  private dragging = false;
  private timers: any[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['round'] || changes['roundKey']) this.reset();
  }
  ngOnDestroy(): void { this.clearTimers(); }

  private reset(): void {
    this.clearTimers();
    this.n = 0;
    this.fillH = 0;
    this.isPouring = false;
    this.solvedState = false;
    this.overflowed = false;
    this.shortfall = false;
    this.dragging = false;
    this.bubbles = this.round ? Array.from({ length: 9 }, () => ({
      x: this.OX + this.tankW * (0.1 + Math.random() * 0.5),
      y: this.OY - 6 - Math.random() * 14,
      r: 3 + Math.random() * 5,
      delay: Math.random() * 2600,
      dur: 2400 + Math.random() * 1900
    })) : [];
  }

  // ── Round shorthands ───────────────────────────────────────────────
  get L(): number { return this.round?.l ?? 3; }
  get W(): number { return this.round?.w ?? 3; }
  get H(): number { return this.round?.h ?? 3; }
  get volume(): number { return this.round?.volume ?? 0; }
  get gaugeMax(): number { return this.round?.gaugeMax ?? 20; }

  // ── Cabinet-oblique projection ─────────────────────────────────────
  get U(): number {
    const needW = this.L + this.W * this.AZX;
    const needH = this.H + this.W * this.AZY;
    return Math.min((this.TX1 - this.TX0) / needW, (this.TY1 - this.TY0) / needH, this.U_MAX);
  }
  get tankW(): number { return (this.L + this.W * this.AZX) * this.U; }
  get tankH(): number { return (this.H + this.W * this.AZY) * this.U; }
  /** Front-bottom-left anchor (tank centred in its region). */
  get OX(): number { return this.TX0 + ((this.TX1 - this.TX0) - this.tankW) / 2; }
  get OY(): number { return this.TY1 - ((this.TY1 - this.TY0) - this.tankH) / 2; }

  private p(x: number, y: number, z: number): { x: number; y: number } {
    return { x: this.OX + x * this.U + z * this.U * this.AZX, y: this.OY - y * this.U - z * this.U * this.AZY };
  }
  private poly(pts: { x: number; y: number }[]): string {
    return pts.map(q => `${q.x.toFixed(1)},${q.y.toFixed(1)}`).join(' ');
  }

  get frontFace(): string { return this.poly([this.p(0, 0, 0), this.p(this.L, 0, 0), this.p(this.L, this.H, 0), this.p(0, this.H, 0)]); }
  get topFace(): string { return this.poly([this.p(0, this.H, 0), this.p(this.L, this.H, 0), this.p(this.L, this.H, this.W), this.p(0, this.H, this.W)]); }
  get rightFace(): string { return this.poly([this.p(this.L, 0, 0), this.p(this.L, this.H, 0), this.p(this.L, this.H, this.W), this.p(this.L, 0, this.W)]); }

  /** Faint unit-cube grid on the three visible faces. */
  get gridLines(): Seg[] {
    const segs: Seg[] = [];
    const add = (a: { x: number; y: number }, b: { x: number; y: number }) => segs.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
    for (let i = 1; i < this.L; i++) add(this.p(i, 0, 0), this.p(i, this.H, 0));   // front verticals
    for (let j = 1; j < this.H; j++) add(this.p(0, j, 0), this.p(this.L, j, 0));   // front horizontals
    for (let i = 0; i <= this.L; i++) add(this.p(i, this.H, 0), this.p(i, this.H, this.W)); // top depth
    for (let k = 0; k <= this.W; k++) add(this.p(0, this.H, k), this.p(this.L, this.H, k)); // top across
    for (let j = 0; j <= this.H; j++) add(this.p(this.L, j, 0), this.p(this.L, j, this.W)); // right depth
    for (let k = 0; k <= this.W; k++) add(this.p(this.L, 0, k), this.p(this.L, this.H, k)); // right verticals
    return segs;
  }

  /** Water sub-box faces at the current fill height. */
  get waterFront(): string { const h = this.fillH; return this.poly([this.p(0, 0, 0), this.p(this.L, 0, 0), this.p(this.L, h, 0), this.p(0, h, 0)]); }
  get waterRight(): string { const h = this.fillH; return this.poly([this.p(this.L, 0, 0), this.p(this.L, h, 0), this.p(this.L, h, this.W), this.p(this.L, 0, this.W)]); }
  get waterTop(): string { const h = this.fillH; return this.poly([this.p(0, h, 0), this.p(this.L, h, 0), this.p(this.L, h, this.W), this.p(0, h, this.W)]); }
  get hasWater(): boolean { return this.fillH > 0.02; }

  private lerp(a: { x: number; y: number }, b: { x: number; y: number }, t: number): { x: number; y: number } {
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  }
  private seg(a: { x: number; y: number }, b: { x: number; y: number }): Seg { return { x1: a.x, y1: a.y, x2: b.x, y2: b.y }; }

  /** Normally-hidden edges, drawn faint so the glass reads as see-through. */
  get hiddenEdges(): Seg[] {
    return [
      this.seg(this.p(0, 0, this.W), this.p(this.L, 0, this.W)),   // back-bottom
      this.seg(this.p(0, 0, 0), this.p(0, 0, this.W)),             // left-bottom
      this.seg(this.p(0, this.H, this.W), this.p(0, 0, this.W))    // back-left vertical
    ];
  }
  /** Bright front edge of the water surface (meniscus highlight). */
  get waterSurfaceEdge(): Seg { return this.seg(this.p(0, this.fillH, 0), this.p(this.L, this.fillH, 0)); }

  /** Soft contact shadow under the tank. */
  get shadowCX(): number { return this.OX + this.tankW * 0.44; }
  get shadowCY(): number { return this.OY + 22; }
  get shadowRX(): number { return this.tankW * 0.5; }

  /** Fish + celebration position (tank centre). */
  get tankCenter(): { x: number; y: number } { return this.p(this.L / 2, this.H / 2, this.W / 2); }

  // ── Gauge ──────────────────────────────────────────────────────────
  get gaugeH(): number { return this.GY_BOT - this.GY_TOP; }
  levelY(units: number): number { return this.GY_BOT - (units / this.gaugeMax) * this.gaugeH; }
  get gaugeLevelY(): number { return this.levelY(this.n); }
  /** Major ticks every 10 up to the max. */
  get gaugeTicks(): number[] {
    const out: number[] = [];
    for (let v = 10; v <= this.gaugeMax; v += 10) out.push(v);
    return out;
  }

  // ── Drag on the gauge ──────────────────────────────────────────────
  onDown(evt: PointerEvent): void {
    if (!this.interactive || this.solvedState || this.isPouring) return;
    evt.preventDefault();
    this.dragging = true;
    this.updateFromEvent(evt);
    try { this.svgRef.nativeElement.setPointerCapture(evt.pointerId); } catch { /* older TVs */ }
  }
  onMove(evt: PointerEvent): void { if (this.dragging) this.updateFromEvent(evt); }
  onUp(evt: PointerEvent): void {
    if (!this.dragging) return;
    this.dragging = false;
    try { this.svgRef.nativeElement.releasePointerCapture(evt.pointerId); } catch { /* ignore */ }
  }
  private updateFromEvent(evt: PointerEvent): void {
    const y = this.toSvgY(evt);
    const units = Math.round(((this.GY_BOT - y) / this.gaugeH) * this.gaugeMax);
    this.n = Math.max(0, Math.min(this.gaugeMax, units));
  }
  nudge(delta: number): void {
    if (!this.interactive || this.solvedState || this.isPouring) return;
    this.n = Math.max(0, Math.min(this.gaugeMax, this.n + delta));
  }

  // ── Pour (called by the shell) ─────────────────────────────────────
  get canPour(): boolean {
    return this.interactive && !this.solvedState && !this.isPouring && this.n > 0;
  }

  pour(): void {
    if (!this.canPour || !this.round) return;
    this.isPouring = true;
    this.overflowed = false;
    this.shortfall = false;
    const base = this.L * this.W;
    const targetH = Math.min(this.H, this.n / base);   // clamp the drawn level to the brim
    this.tween(0, targetH, 30, () => {
      this.isPouring = false;
      if (this.n === this.volume) {
        this.solvedState = true;
        this.solved.emit();
      } else {
        this.overflowed = this.n > this.volume;
        this.shortfall = this.n < this.volume;
        this.missed.emit();
        // let them see the result, then drain for another try
        this.timers.push(setTimeout(() => {
          this.fillH = 0;
          this.overflowed = false;
          this.shortfall = false;
        }, 1300));
      }
    });
  }

  private tween(from: number, to: number, steps: number, done: () => void, i = 0): void {
    this.fillH = from + (to - from) * (i / steps);
    if (i >= steps) { this.fillH = to; done(); return; }
    this.timers.push(setTimeout(() => this.tween(from, to, steps, done, i + 1), 24));
  }

  private toSvgY(evt: PointerEvent): number {
    const svg = this.svgRef.nativeElement;
    const ctm = svg.getScreenCTM();
    if (!ctm) return 0;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    return pt.matrixTransform(ctm.inverse()).y;
  }

  private clearTimers(): void {
    this.timers.forEach(t => clearTimeout(t));
    this.timers = [];
  }
}
